import { Schemas } from "@kollectivia/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ExistingCompanyPicker } from "../../components/ExistingCompanyPicker";
import { InlineContactForm } from "../../components/InlineContactForm";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { loadIntakeDraft, isDraftDirty } from "../../lib/intakeDraftStorage";
import { mapIntakeMvpToCreateProject } from "../../lib/mapIntakeMvpToCreateProject";
import { useCreateCompany } from "../../lib/queries/companies";
import { useContacts } from "../../lib/queries/contacts";
import { useCompanyMandates } from "../../lib/queries/mandates";
import { useCreateProject, useCourtierParDefaut } from "../../lib/queries/projects";
import { useCreateQuoteRequest } from "../../lib/queries/quoteRequests";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

export function StartRequestPage() {
  const [params] = useSearchParams();
  return <StartRequestContent key={params.get("company_id") ?? ""} />;
}

function StartRequestContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [company, setCompany] = useState<string | undefined>(
    () => params.get("company_id") || undefined,
  );
  const [legacyDraft] = useState(() => loadIntakeDraft("mvp"));
  const [contact, setContact] = useState<string>();
  const [contactTouched, setContactTouched] = useState(false);
  const [addCompany, setAddCompany] = useState(false);
  const [addContact, setAddContact] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [createdProject, setCreatedProject] = useState<string>();
  const contacts = useContacts(company, { limit: 100 });
  const mandates = useCompanyMandates(company);
  const broker = useCourtierParDefaut(company);
  const companyCreate = useCreateCompany();
  const projectCreate = useCreateProject();
  const requestCreate = useCreateQuoteRequest();
  const items = contacts.data?.items ?? [];
  const currentMandate = mandates.data?.items.find((m) => !m.revoked_at);
  const validMandate = !!currentMandate?.is_valid && !!currentMandate.artifact_available;
  const selected = items.find((c) => c.id === contact);
  const reachable =
    !!selected?.email &&
    Schemas.CreateContactSchema.safeParse({
      company_id: company,
      full_name: selected.full_name,
      email: selected.email,
    }).success;
  const busy = projectCreate.isPending || requestCreate.isPending || companyCreate.isPending;
  useEffect(() => {
    if (contactTouched || contact) return;
    const primary = (contacts.data?.items ?? []).filter((c) => c.is_primary);
    if (primary.length === 1) setContact(primary[0]!.id);
  }, [contacts.data, contactTouched, contact]);
  const { allowNavigation } = useUnsavedChangesGuard(
    !!(
      title ||
      companyName ||
      createdProject ||
      contactTouched ||
      company !== (params.get("company_id") || undefined)
    ),
    t("preparation_context.unsaved"),
    "company_id",
  );
  function showError(err: unknown) {
    setError(
      err instanceof ApiException
        ? getApiErrorMessage(err, normalizeLang(i18n.language))
        : t("errors.generic"),
    );
  }
  async function createCompany(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const row = await companyCreate.mutateAsync({ legal_name: companyName.trim() });
      setCompany(row.id);
      setContact(undefined);
      setContactTouched(false);
      setAddCompany(false);
    } catch (err) {
      showError(err);
    }
  }
  async function next() {
    if (!company || !title.trim() || (!validMandate && !reachable) || busy) return;
    setError(null);
    try {
      // Si la seconde création échoue, la reprise réutilise le dossier déjà créé.
      const projectId =
        createdProject ??
        (
          await projectCreate.mutateAsync(
            mapIntakeMvpToCreateProject({
              company_id: company,
              contact_id: contact,
              broker_id: broker.data?.broker_id ?? undefined,
              title: title.trim(),
            }),
          )
        ).id;
      setCreatedProject(projectId);
      const request = await requestCreate.mutateAsync({ input: { project_id: projectId } });
      allowNavigation();
      navigate(`/projects/${projectId}?view=preparation&request=${request.id}`);
    } catch (err) {
      showError(err);
    }
  }
  return (
    <div className="mx-auto w-full space-y-4">
      <header className="pt-4">
        <h1 className="text-3xl font-semibold">{t("nav.new_request")}</h1>
        <p className="mt-2 text-slate-600">{t("request_flow.client_help")}</p>
      </header>
      {legacyDraft && isDraftDirty(legacyDraft.data) && (
        <Link
          to="/projects/new"
          className="text-brand-700 inline-flex min-h-11 items-center underline"
        >
          {t("foundation.resume_legacy")}
        </Link>
      )}
      <fieldset disabled={busy || !!createdProject} className="space-y-4">
        <label className="block" htmlFor="request-company">
          <span className="label">{t("projects.fields.company")}</span>
          <ExistingCompanyPicker
            id="request-company"
            value={company}
            onChange={(id) => {
              setCompany(id);
              setContact(undefined);
              setContactTouched(false);
            }}
          />
        </label>
        {!addCompany && (
          <button type="button" className="btn min-h-11" onClick={() => setAddCompany(true)}>
            {t("request_flow.add_company")}
          </button>
        )}
        {addCompany && (
          <form onSubmit={(e) => void createCompany(e)} className="space-y-3 rounded-lg border p-4">
            <label className="block">
              {t("request_flow.company_name")}
              <input
                className="input"
                required
                name="legal_name"
                autoComplete="organization"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </label>
            <button className="btn-primary min-h-11" type="submit">
              {t("common.save")}
            </button>
            <button className="btn min-h-11" type="button" onClick={() => setAddCompany(false)}>
              {t("common.cancel")}
            </button>
          </form>
        )}
        {company && (
          <>
            <label className="block">
              <span className="label">{t("projects.fields.contact")}</span>
              <select
                className="input"
                value={contact ?? ""}
                onChange={(e) => {
                  setContact(e.target.value || undefined);
                  setContactTouched(true);
                }}
              >
                <option value="">{t("request_flow.choose_contact")}</option>
                {items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name}
                    {c.is_primary ? ` — ${t("request_flow.primary_contact")}` : ""}
                    {!c.email ? ` — ${t("request_flow.no_email")}` : ""}
                  </option>
                ))}
              </select>
            </label>
            {contacts.isError && <p role="alert">{t("errors.generic")}</p>}
            {!validMandate && !reachable && (
              <p role="status" className="text-amber-800">
                {t("request_flow.contact_required")}
              </p>
            )}
            {!addContact && (
              <button type="button" className="btn min-h-11" onClick={() => setAddContact(true)}>
                {t("request_flow.add_contact")}
              </button>
            )}
            {addContact && (
              <InlineContactForm
                companyId={company}
                onCreated={(id) => {
                  setContact(id);
                  setContactTouched(true);
                  setAddContact(false);
                }}
                onCancel={() => setAddContact(false)}
              />
            )}
          </>
        )}
        <label className="block">
          <span className="label">{t("request_flow.file_title")}</span>
          <input
            className="input"
            name="file_title"
            autoComplete="off"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>
      </fieldset>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {mandates.isError && <p role="alert">{t("request_flow.authorization_unavailable")}</p>}
      <div className="workspace-action-bar">
        <button
          type="button"
          onClick={() => void next()}
          disabled={
            !company ||
            !title.trim() ||
            busy ||
            mandates.isPending ||
            mandates.isError ||
            (!validMandate && !reachable)
          }
          className="btn-primary min-h-11"
        >
          {t("request_flow.continue_authorization")}
        </button>
      </div>
    </div>
  );
}
